import React, { useState, useEffect, KeyboardEvent, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RefreshProjectProps } from "../../types/screensTypes";
import * as Yup from "yup";
import { getWaitingAudioAI, createTextQueueAI, createProjectAI, addItemToProjectAI, getAdminInfo } from "../../api";
import { getRagFolders } from "../../api/ragFolders";
import { getBrandVoices, BrandVoice } from "../../api/brandVoice";
import { getAudienceProfiles, AudienceProfile } from "../../api/audienceProfile";
import AddToProjectModal from "../../components/modals/AddToProjectModal";
import CreateBrandVoiceModal from "../../components/modals/CreateBrandVoiceModal";
import EditAudienceProfileModal from "../resources/audienceProfiles/EditAudienceProfileModal";
import { useGlobalState } from "../../context/useGlobalState";
import { User } from "../../types/types";
import { RagFolder } from "../../types/requestsOutputs";
import Button from "../../components/ui/Button";
import TextInput from "../../components/ui/TextInput";
import AddToProjectButton from "../../components/ui/AddToProjectButton";
import { Tooltip } from "@mui/material";
import AIGenerationStatusModal from "../../components/modals/AIGenerationStatusModal";
import useCancelGeneration from "../../hooks/useCancelGeneration";
import Tag from "../../components/common/Tag";
import { FastField, Field, Formik, useFormikContext } from "formik";
import { DropdownField, MultiSelectDropdownField, NumberField, TextField } from "../../components/common/FormFields";
import FormGroup from "../../components/common/FormGroup";
import { COMPANY_ADDONS, hasAddon } from "../../utils/addons";
import { AI_MODEL_TYPES } from "../../constants";
import { useRemainingTokens } from "../../hooks/useRemainingTokens";
import { isQuotaError } from "../../utils/quotaError";
import Icon from "../../components/common/Icon";
import styles from "./TextCreation.module.css";
import stepStyles from "../track/create/createStep.module.css";

interface FormValues {
    dataSource: "general_knowledge" | "database_only" | "database_inspired" | "web_search" | "database_and_web";
    selectedContentLanguage: string;
    textObject: string;
    textTitle: string;
    audioDuration: { minutes: number; seconds: number };
    audience: string;
    numberStories: number;
    requiredContent: string[];
    toneOfVoice: string;
    selectedFolders: string[];
    brandVoiceId: string;
    audienceProfileId: string;
}

const TextCreationForm: React.FC<{
    handleAudioAIAwaiting: (id: string) => Promise<void>;
    id?: string;
    user?: User | null;
    nonEmptyRagFolders: RagFolder[];
    brandVoices: BrandVoice[];
    audienceProfiles: AudienceProfile[];
    isCreateBrandVoiceModalOpen: boolean;
    onOpenCreateBrandVoiceModal: () => void;
    onCloseCreateBrandVoiceModal: () => void;
    onBrandVoiceCreated: (brandVoiceId: string) => void;
    isCreateAudienceProfileModalOpen: boolean;
    onOpenCreateAudienceProfileModal: () => void;
    onCloseCreateAudienceProfileModal: () => void;
    onAudienceProfileCreated: (audienceProfileId: string) => void;
}> = ({
    handleAudioAIAwaiting,
    id,
    user,
    nonEmptyRagFolders,
    brandVoices,
    audienceProfiles,
    isCreateBrandVoiceModalOpen,
    onOpenCreateBrandVoiceModal,
    onCloseCreateBrandVoiceModal,
    onBrandVoiceCreated,
    isCreateAudienceProfileModalOpen,
    onOpenCreateAudienceProfileModal,
    onCloseCreateAudienceProfileModal,
    onAudienceProfileCreated,
}) => {
    // This callback will be handled in the parent component
    const { values, setFieldValue, resetForm } = useFormikContext<FormValues>();

    const { dataSource, numberStories, requiredContent, brandVoiceId } = values;

    // Helper to check if dataSource uses database
    const usesDatabase =
        dataSource === "database_only" || dataSource === "database_inspired" || dataSource === "database_and_web";

    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data } = useGlobalState();

    const [contentInput, setContentInput] = useState<string>("");

    const sortedLanguages = useMemo(() => {
        if (!data?.languagesAI) return [];
        return [...data.languagesAI].sort((a, b) => a.label.localeCompare(b.label));
    }, [data?.languagesAI]);

    const canAddContent =
        contentInput.trim() !== "" && requiredContent.length < numberStories && contentInput.length <= 80;

    const handleAddContent = useCallback(() => {
        if (contentInput.trim() === "" || requiredContent.length >= numberStories || contentInput.length > 80) {
            return;
        }
        setFieldValue("requiredContent", [...requiredContent, contentInput.trim()]);
        setContentInput("");
    }, [contentInput, numberStories, requiredContent, setFieldValue]);

    /** Adds a required content item on Enter (creer-b tag-input). */
    const handleContentKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddContent();
        }
    };

    const handleRemoveContent = (index: number) => {
        const updatedContent = [...requiredContent];
        updatedContent.splice(index, 1);
        setFieldValue("requiredContent", updatedContent);
    };

    const hasRequiredContentError = useMemo(
        () => requiredContent.length > numberStories,
        [requiredContent, numberStories],
    );

    useEffect(() => {
        if (id && id.trim() !== "") {
            handleAudioAIAwaiting(id);
        } else {
            resetForm();
        }
    }, [id, handleAudioAIAwaiting, resetForm]);

    // Auto-select all folders when database is used
    useEffect(() => {
        if (usesDatabase && nonEmptyRagFolders.length > 0) {
            const allFolderIds = nonEmptyRagFolders.map((folder) => folder.id);
            setFieldValue("selectedFolders", allFolderIds);
        } else if (!usesDatabase) {
            // Clear selectedFolders when database is not used
            setFieldValue("selectedFolders", []);
        }
    }, [usesDatabase, nonEmptyRagFolders, setFieldValue]);

    return (
        <div className={stepStyles.stepDescription}>
            {/* Language */}
            <FormGroup
                name="selectedContentLanguage"
                title={t("language")}
                style={{ marginTop: 0, maxWidth: "220px" }}
                size="small"
                required
            >
                <Field
                    name="selectedContentLanguage"
                    label={t("textCreationLanguageHelpText")}
                    component={DropdownField}
                    options={sortedLanguages}
                    loading={false}
                    error={false}
                    placeholder={t("selectALanguage")}
                    isWithoutMargin={true}
                />
            </FormGroup>

            {/* Sources */}
            {user?.cmpany && hasAddon(user.cmpany, COMPANY_ADDONS.DATABASE) && (
                <FormGroup
                    name="dataSource"
                    title={t("sources")}
                    subtitle={t("howDoYouWantAIToCreateContent")}
                    style={{ marginTop: 20, maxWidth: "220px" }}
                    size="small"
                    disabledTooltip={nonEmptyRagFolders.length === 0 ? t("noNonEmptyRagFoldersAvailable") : ""}
                    required
                >
                    <Field
                        name="dataSource"
                        component={DropdownField}
                        options={[
                            {
                                value: "general_knowledge",
                                label: t("fromGeneralKnowledge"),
                            },
                            {
                                value: "database_only",
                                label: t("onlyFromMyDocuments"),
                                isDisabled: nonEmptyRagFolders.length === 0,
                            },
                            {
                                value: "database_inspired",
                                label: t("inspiredByMyDocuments"),
                                isDisabled: nonEmptyRagFolders.length === 0,
                            },
                            {
                                value: "web_search",
                                label: t("fromRecentWebInformation"),
                                isDisabled: true, // Grayed out for now
                            },
                            {
                                value: "database_and_web",
                                label: t("combiningDocumentsAndWeb"),
                                isDisabled: true, // Grayed out for now
                            },
                        ]}
                        error={false}
                        placeholder={t("selectAnOption")}
                        isWithoutMargin={true}
                    />
                    {dataSource && (
                        <div style={{ marginTop: "8px", fontSize: "12px", color: "#666", fontStyle: "italic" }}>
                            {dataSource === "general_knowledge"
                                ? t("fromGeneralKnowledgeDescription")
                                : dataSource === "database_only"
                                  ? t("onlyFromMyDocumentsDescription")
                                  : dataSource === "database_inspired"
                                    ? t("inspiredByMyDocumentsDescription")
                                    : dataSource === "web_search"
                                      ? t("fromRecentWebInformationDescription")
                                      : dataSource === "database_and_web"
                                        ? t("combiningDocumentsAndWebDescription")
                                        : ""}
                        </div>
                    )}
                </FormGroup>
            )}

            {/* Folder Selection */}
            {usesDatabase && nonEmptyRagFolders.length > 0 && (
                <FormGroup
                    name="selectedFolders"
                    title={t("selectFolders")}
                    subtitle={t("selectFoldersToFilter")}
                    style={{ marginTop: 20, maxWidth: "220px" }}
                    size="small"
                    required
                >
                    <Field
                        name="selectedFolders"
                        component={MultiSelectDropdownField}
                        placeholder={t("selectFoldersPlaceholder")}
                        options={nonEmptyRagFolders.map((folder) => ({
                            value: folder.id,
                            label: t("folderWithFileCount", {
                                name: folder.name,
                                count: folder.fileCount,
                            }),
                        }))}
                        onChange={(selectedValues: string[]) => {
                            setFieldValue("selectedFolders", selectedValues);
                        }}
                        value={values.selectedFolders}
                        isWithoutMargin
                    />
                </FormGroup>
            )}

            <FormGroup
                name="numberStories"
                title={t("numberStories")}
                subtitle={t("numberStoriesHelptext")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
                required
            >
                <FastField component={NumberField} name="numberStories" min={1} max={20} />
            </FormGroup>

            <FormGroup
                name="textTitle"
                title={t("title")}
                subtitle={t("textCreationTitleSubtitle")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
                required
            >
                <FastField
                    name="textTitle"
                    component={TextField}
                    placeholder={t("titleOfTheText")}
                    size={"small"}
                    style={{ marginTop: "8px" }}
                />
            </FormGroup>

            {/* Overall Topic */}
            <FormGroup
                name="textObject"
                title={t("topic")}
                subtitle={t("whatIsGeneralTopic")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
                required
            >
                <Field name="textObject" component={TextField} placeholder={t("topicExample")} />
            </FormGroup>

            <FormGroup
                title={t("requiredSpecificContent")}
                subtitle={t("specifyRequiredContentLabel")}
                tooltip={t("requiredContentInfo")}
                size="small"
            >
                <div className={styles.enterHint}>{t("pressEnterToAddContent")}</div>
                <Tooltip
                    title={requiredContent.length >= numberStories ? t("maxContentItemsReached") : ""}
                    arrow
                    placement="top"
                >
                    <div
                        className={styles.tagInput}
                        style={{
                            cursor: requiredContent.length >= numberStories ? "not-allowed" : undefined,
                        }}
                    >
                        <TextInput
                            placeholder={t("specifyRequiredContentExample")}
                            value={contentInput}
                            size="small"
                            onChange={(e) => setContentInput(e.target.value)}
                            onKeyDown={handleContentKeyDown}
                            disabled={requiredContent.length >= numberStories}
                        />
                        <Button
                            label={t("add")}
                            variant="soft"
                            isSmall
                            className={styles.tagAddBtn}
                            disabled={!canAddContent}
                            onClick={handleAddContent}
                        />
                    </div>
                </Tooltip>
                {contentInput.length > 80 && (
                    <div className={styles.contentWarning}>
                        {t("contentItemTooLong", { count: contentInput.length })}
                    </div>
                )}
                <div className={`${styles.tagMeta}${hasRequiredContentError ? ` ${styles.tagMetaError}` : ""}`}>
                    <span>
                        {contentInput.length}/80 {t("charactersLimit")}
                    </span>
                    <span>
                        {requiredContent.length}/{numberStories} {t("specified")}
                    </span>
                </div>
                {requiredContent.length > 0 && (
                    <div className={styles.tagList}>
                        {requiredContent.map((item, index) => (
                            <Tag
                                key={`content-${index}`}
                                text={item}
                                onRemove={() => handleRemoveContent(index)}
                                backgroundColor={hasRequiredContentError ? "var(--danger)" : "var(--green-100)"}
                                color={hasRequiredContentError ? "#fff" : "var(--green-800)"}
                            />
                        ))}
                    </div>
                )}
                {hasRequiredContentError && (
                    <div className={styles.contentWarning}>{t("tooManyRequiredContentItems")}</div>
                )}
            </FormGroup>

            {/* Audio Duration */}
            <FormGroup
                name="audioDuration"
                title={t("audioDuration")}
                subtitle={t("audioDurationHelptext")}
                style={{ marginTop: 0, maxWidth: "220px" }}
                size="small"
                required
            >
                <div style={{ marginTop: "5px", display: "flex", gap: "10px", alignItems: "center" }}>
                    <Field name="audioDuration.minutes" component={NumberField} placeholder="minutes" />
                    <span style={{ fontSize: "13px" }}>min</span>
                    <Field name="audioDuration.seconds" component={NumberField} placeholder="seconds" />
                    <span style={{ fontSize: "13px" }}>{t("seconds")}</span>
                </div>
            </FormGroup>

            {/* Audience Profile */}
            <FormGroup
                name="audienceProfileId"
                title={t("audienceProfile")}
                subtitle={t("audienceProfileHelpText")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <Field
                        name="audienceProfileId"
                        component={DropdownField}
                        options={[
                            { value: "__none__", label: t("noAudience") },
                            ...audienceProfiles.map((ap) => ({
                                value: ap.id,
                                label: ap.name,
                            })),
                            ...(audienceProfiles.length < 10
                                ? [{ value: "create_new", label: t("createNewAudienceProfile") || "Create New..." }]
                                : []),
                        ]}
                        error={false}
                        placeholder={t("noAudience")}
                        isWithoutMargin={true}
                        onChange={(value: string) => {
                            if (value === "create_new") {
                                onOpenCreateAudienceProfileModal();
                                setFieldValue("audienceProfileId", "");
                            } else {
                                setFieldValue("audienceProfileId", !value || value === "__none__" ? "" : value);
                            }
                        }}
                    />
                    {values.audienceProfileId && values.audienceProfileId !== "" && (
                        <div style={{ fontSize: "12px", color: "#666", fontStyle: "italic" }}>
                            {t("audienceProfileSelected")}:{" "}
                            {audienceProfiles.find((ap) => ap.id === values.audienceProfileId)?.name}
                        </div>
                    )}
                </div>
            </FormGroup>

            {/* Writing Style (formerly Brand Voice) */}
            <FormGroup
                name="brandVoiceId"
                title={t("brandVoice")}
                subtitle={t("brandVoiceHelpText")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <Field
                        name="brandVoiceId"
                        component={DropdownField}
                        options={[
                            { value: "__none__", label: t("noVoice") },
                            ...brandVoices.map((bv) => ({
                                value: bv.id,
                                label: bv.name,
                            })),
                            ...(brandVoices.length < 10
                                ? [{ value: "create_new", label: t("createNewBrandVoice") || "Create New..." }]
                                : []),
                        ]}
                        error={false}
                        placeholder={t("noVoice")}
                        isWithoutMargin={true}
                        onChange={(value: string) => {
                            if (value === "create_new") {
                                onOpenCreateBrandVoiceModal();
                                setFieldValue("brandVoiceId", "");
                            } else {
                                setFieldValue("brandVoiceId", !value || value === "__none__" ? "" : value);
                            }
                        }}
                    />
                    {brandVoiceId && brandVoiceId !== "" && (
                        <div style={{ fontSize: "12px", color: "#666", fontStyle: "italic" }}>
                            {t("brandVoiceSelected")}: {brandVoices.find((bv) => bv.id === brandVoiceId)?.name}
                        </div>
                    )}
                </div>
            </FormGroup>
        </div>
    );
};

const TextCreation: React.FC<RefreshProjectProps> = ({ refreshProjects }) => {
    const { t: rawT, i18n } = useTranslation();

    const t = rawT as any;
    const { id } = useParams<{ id?: string }>();
    const navigate = useNavigate();

    const [user, setUser] = useState<any>(null);
    const [textGenerated, setTextGenerated] = useState("");
    const [loadingTextGeneration, setLoadingTextGeneration] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [waitingRank, setWaitingRank] = useState<number | null>(null);
    const [generationStatus, setGenerationStatus] = useState<"waiting" | "success" | "error">("waiting");
    const [errorReason, setErrorReason] = useState<"generic" | "quota">("generic");
    const { isTextExhausted, refetch: refetchTokens, data: tokenData } = useRemainingTokens();
    const { cancelling, cancelGeneration, confirmCanceled, resetCancelling } = useCancelGeneration();
    const [nonEmptyRagFolders, setNonEmptyRagFolders] = useState<RagFolder[]>([]);
    const [isInProject, setIsInProject] = useState<boolean>(false);
    const [brandVoices, setBrandVoices] = useState<BrandVoice[]>([]);
    const [audienceProfiles, setAudienceProfiles] = useState<AudienceProfile[]>([]);
    const [isCreateBrandVoiceModalOpen, setIsCreateBrandVoiceModalOpen] = useState(false);
    const [isCreateAudienceProfileModalOpen, setIsCreateAudienceProfileModalOpen] = useState(false);
    const setFieldValueRef = useRef<((field: string, value: any) => void) | null>(null);
    /**
     * Generation launched from this page, until its outcome is shown. Opening a text any other
     * way (drafts, browser back) must not pop the status modal for a generation long over.
     */
    const launchedIdRef = useRef<string | null>(null);

    useEffect(() => {
        const fetchUser = async () => {
            const token = localStorage.getItem("token");
            if (!token) return;
            try {
                const response = await getAdminInfo({ token });
                setUser(response);
            } catch (err) {
                console.error(err);
            }
        };

        fetchUser();
    }, []);

    useEffect(() => {
        const fetchRagFolders = async () => {
            const token = localStorage.getItem("token") || "";
            try {
                const folders = await getRagFolders({ token });
                // Filter non-empty folders
                const nonEmpty = folders.filter((folder) => !folder.isEmpty);
                setNonEmptyRagFolders(nonEmpty);
            } catch (err) {
                console.error(err);
            }
        };

        if (user?.cmpany && hasAddon(user.cmpany, COMPANY_ADDONS.DATABASE)) {
            fetchRagFolders();
        }
    }, [user]);

    useEffect(() => {
        const fetchBrandVoices = async () => {
            try {
                const voices = await getBrandVoices();
                setBrandVoices(voices);
            } catch (err) {
                console.error("Error fetching brand voices:", err);
            }
        };

        fetchBrandVoices();
    }, []);

    useEffect(() => {
        const fetchAudienceProfiles = async () => {
            try {
                const profiles = await getAudienceProfiles();
                setAudienceProfiles(profiles);
            } catch (err) {
                console.error("Error fetching audience profiles:", err);
            }
        };

        fetchAudienceProfiles();
    }, []);

    const handleAudioAIAwaiting = useCallback(
        async (id: string) => {
            // The modal and the outcome (success, failure, canceled) only concern a generation
            // launched from this page, or one still running when the page opens.
            let showOutcome = launchedIdRef.current === id;
            const concludeLaunch = () => {
                if (launchedIdRef.current === id) launchedIdRef.current = null;
            };

            setLoadingTextGeneration(true);
            setShowStatusModal(showOutcome);
            setGenerationStatus("waiting");
            setWaitingRank(null);

            const fetchAudioAI = async () => {
                try {
                    const audioAI = await getWaitingAudioAI({
                        token: localStorage.getItem("token") || "",
                        id,
                    });

                    if (audioAI?.failed) {
                        setLoadingTextGeneration(false);
                        setGenerationStatus("error");
                        resetCancelling();
                        concludeLaunch();
                        return;
                    }

                    // The form is still filled in: closing the modal is enough to relaunch.
                    if (audioAI?.canceled) {
                        setLoadingTextGeneration(false);
                        setShowStatusModal(false);
                        setWaitingRank(null);
                        if (showOutcome) confirmCanceled();
                        else resetCancelling();
                        concludeLaunch();
                        return;
                    }

                    if (audioAI?.waiting) {
                        // Still running: from here on, its outcome is worth showing.
                        if (!showOutcome) {
                            showOutcome = true;
                            setShowStatusModal(true);
                        }
                        if (typeof audioAI.rank === "number") {
                            setWaitingRank(audioAI.rank);
                        }
                        setTimeout(fetchAudioAI, 5000);
                    } else {
                        setLoadingTextGeneration(false);
                        if (audioAI?.audioAI) {
                            if (audioAI.audioAI.text === "Nothing found") {
                                setTextGenerated(t("nothingFoundText"));
                            } else {
                                setTextGenerated(audioAI.audioAI.text);
                            }
                            // Check if audioAI is already in a project
                            setIsInProject(!!audioAI.audioAI.projectAIId);
                            setGenerationStatus("success");
                            refetchTokens();
                        }
                        setWaitingRank(null);
                        resetCancelling();
                        concludeLaunch();
                    }
                } catch (error) {
                    console.error("Error fetching audio AI data:", error);
                    setLoadingTextGeneration(false);
                    // A text that cannot be read is worth reporting however the page was opened.
                    setShowStatusModal(true);
                    setGenerationStatus("error");
                    resetCancelling();
                    concludeLaunch();
                }
            };

            fetchAudioAI();
        },
        [
            setLoadingTextGeneration,
            setShowStatusModal,
            setGenerationStatus,
            setWaitingRank,
            setTextGenerated,
            t,
            refetchTokens,
            confirmCanceled,
            resetCancelling,
        ],
    );

    const validationSchema = Yup.object({
        dataSource: Yup.string()
            .oneOf(["general_knowledge", "database_only", "database_inspired", "web_search", "database_and_web"])
            .required(t("dataSourceRequired")),
        selectedContentLanguage: Yup.string().required(t("languageRequired")),
        textObject: Yup.string().required(t("topicRequired")),
        textTitle: Yup.string().required(t("titleRequired")),
        audioDuration: Yup.object({
            minutes: Yup.number().min(0),
            seconds: Yup.number().min(0),
        }).test("duration", t("audioDurationRequired"), (value) => {
            return (value?.minutes || 0) + (value?.seconds || 0) > 0;
        }),
        audience: Yup.string(),
        numberStories: Yup.number().min(1).max(20),
        requiredContent: Yup.array().of(Yup.string().max(80)),
        toneOfVoice: Yup.string(),
        selectedFolders: Yup.array()
            .of(Yup.string())
            .when("dataSource", {
                is: (value: string) =>
                    value === "database_only" || value === "database_inspired" || value === "database_and_web",
                then: (schema) => schema.min(1, t("atLeastOneFolderMustBeSelected")),
                otherwise: (schema) => schema.notRequired(),
            }),
    });

    const initialValues: FormValues = {
        dataSource: "general_knowledge",
        selectedContentLanguage: "",
        textObject: "",
        textTitle: "",
        audioDuration: { minutes: 0, seconds: 0 },
        audience: "",
        numberStories: 1,
        requiredContent: [],
        toneOfVoice: "",
        selectedFolders: [],
        brandVoiceId: "",
        audienceProfileId: "",
    };

    const handleSubmit = async (values: FormValues) => {
        setLoadingTextGeneration(true);
        setShowStatusModal(true);
        setGenerationStatus("waiting");
        setErrorReason("generic");

        // Map dataSource to connectDatabase and restrictToDatabase
        const usesDatabase =
            values.dataSource === "database_only" ||
            values.dataSource === "database_inspired" ||
            values.dataSource === "database_and_web";
        const restrictToDatabase = values.dataSource === "database_only";

        try {
            const response = await createTextQueueAI({
                token: localStorage.getItem("token") || "",
                isFromDb: usesDatabase,
                language: values.selectedContentLanguage,
                textObject: values.textObject,
                audioDurationMin: values.audioDuration.minutes,
                audioDurationSec: values.audioDuration.seconds,
                audience: values.audience,
                hasToneOfVoice: !!values.toneOfVoice,
                toneOfVoice: values.toneOfVoice,
                textTitle: values.textTitle,
                numberStories: values.numberStories,
                requiredContent: values.requiredContent,
                textType: "history",
                queryFilters: values.selectedFolders.length > 0 ? { folderId: values.selectedFolders } : undefined,
                restrictToDatabase: restrictToDatabase,
                brandVoiceId: values.brandVoiceId && values.brandVoiceId !== "" ? values.brandVoiceId : null,
                audienceProfileId:
                    values.audienceProfileId && values.audienceProfileId !== "" ? values.audienceProfileId : null,
            });

            if (response?.id) {
                launchedIdRef.current = response.id;
                navigate(`/create/text/${response.id}`);
            } else {
                setGenerationStatus("error");
            }
        } catch (err) {
            if (isQuotaError(err)) {
                setErrorReason("quota");
                refetchTokens();
            }
            setGenerationStatus("error");
        } finally {
            setLoadingTextGeneration(false);
        }
    };

    const handleNextStep = () => {
        navigate(`/create/text-remix/${id || ""}`);
    };

    const handleAddToProject = async (projectId: string, newProjectName: string) => {
        const token = localStorage.getItem("token");
        if (!token || !id) {
            return;
        }

        const audioAI = await getWaitingAudioAI({ token: token, id: id });

        if (audioAI?.audioAI) {
            if (projectId === "new") {
                await createProjectAI({
                    token: token,
                    name: newProjectName,
                    audioAIId: audioAI.audioAI.id,
                });
            } else {
                await addItemToProjectAI({
                    token: token,
                    projectId: projectId,
                    itemId: audioAI.audioAI.id,
                    itemType: AI_MODEL_TYPES.AUDIO,
                });
            }

            await refreshProjects();
            // Refresh the audioAI to check if it's now in a project
            if (id) {
                const updatedAudioAI = await getWaitingAudioAI({ token: token, id: id });
                if (updatedAudioAI?.audioAI) {
                    setIsInProject(!!updatedAudioAI.audioAI.projectAIId);
                }
            }
        }
    };

    const remainingWords = tokenData?.remainigTextTokens;

    return (
        <div className={styles.page}>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                key={i18n.language}
                validateOnMount
            >
                {({ isSubmitting, submitForm, errors, values: { textTitle }, setFieldValue }) => {
                    setFieldValueRef.current = setFieldValue;

                    return (
                        <>
                            <div className={styles.work}>
                                <section className={styles.editor}>
                                    <div className={styles.editorHead}>
                                        <div>
                                            <h1>{t("textGeneration")}</h1>
                                            <p>{t("fillThisSectionToGenerateYourStoryYouCanEditItManuallyLater")}</p>
                                        </div>
                                        {remainingWords != null && (
                                            <span className={styles.token}>
                                                <Icon name="ic-sparkles" />
                                                {t("remainingWords")}&nbsp;: <b>{remainingWords.toLocaleString()}</b>
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.canvas}>
                                        {!textGenerated && !loadingTextGeneration ? (
                                            <div className={styles.canvasEmpty}>
                                                <div className={styles.empty}>
                                                    <div className={styles.emptyIco}>
                                                        <Icon name="ic-file-text" />
                                                    </div>
                                                    <h3>{t("yourTextWillAppearHere")}</h3>
                                                    <p>{t("yourTextWillAppearHereWhenGenerationIsDone")}</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className={styles.canvasEditor}>
                                                <TextInput
                                                    customClassName="text-input-big"
                                                    placeholder={
                                                        loadingTextGeneration
                                                            ? t("pleaseWaitTextGenerationInProgress")
                                                            : t("yourTextWillAppearHereWhenGenerationIsDone")
                                                    }
                                                    value={loadingTextGeneration ? "" : textGenerated}
                                                    numberOfLines={12}
                                                    size="big"
                                                    onChange={(e) => setTextGenerated(e.target.value)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </section>

                                <aside className={styles.params}>
                                    <div className={styles.paramsHead}>
                                        <Icon name="ic-settings" />
                                        {t("settings")}
                                    </div>
                                    <div className={styles.paramsBody}>
                                        <TextCreationForm
                                            handleAudioAIAwaiting={handleAudioAIAwaiting}
                                            id={id}
                                            user={user}
                                            nonEmptyRagFolders={nonEmptyRagFolders}
                                            brandVoices={brandVoices}
                                            audienceProfiles={audienceProfiles}
                                            isCreateBrandVoiceModalOpen={isCreateBrandVoiceModalOpen}
                                            onOpenCreateBrandVoiceModal={() => setIsCreateBrandVoiceModalOpen(true)}
                                            onCloseCreateBrandVoiceModal={() => setIsCreateBrandVoiceModalOpen(false)}
                                            onBrandVoiceCreated={async () => {
                                                const voices = await getBrandVoices();
                                                setBrandVoices(voices);
                                            }}
                                            isCreateAudienceProfileModalOpen={isCreateAudienceProfileModalOpen}
                                            onOpenCreateAudienceProfileModal={() =>
                                                setIsCreateAudienceProfileModalOpen(true)
                                            }
                                            onCloseCreateAudienceProfileModal={() =>
                                                setIsCreateAudienceProfileModalOpen(false)
                                            }
                                            onAudienceProfileCreated={async () => {
                                                const profiles = await getAudienceProfiles();
                                                setAudienceProfiles(profiles);
                                            }}
                                        />
                                    </div>
                                    <div className={styles.paramsFoot}>
                                        <Tooltip
                                            title={
                                                isTextExhausted
                                                    ? t("quotaReachedTextTooltip")
                                                    : Object.keys(errors).length > 0
                                                      ? t("formIncompleteTooltip", {
                                                            fields: Object.values(errors).join(", "),
                                                        })
                                                      : ""
                                            }
                                            placement="top"
                                        >
                                            <div>
                                                <Button
                                                    label={
                                                        loadingTextGeneration
                                                            ? waitingRank !== null && waitingRank > 0
                                                                ? t("waitingInQueue", {
                                                                      rank: waitingRank,
                                                                  })
                                                                : t("generating")
                                                            : t("generateText")
                                                    }
                                                    onClick={submitForm}
                                                    disabled={
                                                        isSubmitting ||
                                                        Object.keys(errors).length > 0 ||
                                                        isTextExhausted
                                                    }
                                                    variant="primary"
                                                    shape="default"
                                                    block
                                                    className={styles.generateBtn}
                                                    icon={
                                                        !loadingTextGeneration ? (
                                                            <Icon name="ic-sparkles" className="ic" />
                                                        ) : undefined
                                                    }
                                                />
                                            </div>
                                        </Tooltip>
                                        <span className={styles.hint}>{t("youCanEditTextBeforeAudio")}</span>
                                        <div className={styles.secondaryActions}>
                                            {textGenerated && (
                                                <Button
                                                    label={t("nextStep")}
                                                    onClick={handleNextStep}
                                                    variant="ghost"
                                                />
                                            )}
                                            {textGenerated &&
                                                textGenerated !== "" &&
                                                textTitle &&
                                                textTitle !== "" &&
                                                !isInProject && (
                                                    <AddToProjectButton onClick={() => setIsModalOpen(true)} />
                                                )}
                                        </div>
                                    </div>
                                </aside>
                            </div>

                            <AddToProjectModal
                                isOpen={isModalOpen}
                                onClose={() => setIsModalOpen(false)}
                                onAddToProject={handleAddToProject}
                            />
                            <AIGenerationStatusModal
                                isOpen={showStatusModal}
                                onClose={() => setShowStatusModal(false)}
                                status={generationStatus}
                                rank={waitingRank}
                                errorTitle={errorReason === "quota" ? t("quotaReachedTitle") : undefined}
                                errorSubtitle={errorReason === "quota" ? t("quotaReachedTextSubtitle") : undefined}
                                onCancel={id ? () => cancelGeneration(id) : undefined}
                                cancelling={cancelling}
                            />
                            <CreateBrandVoiceModal
                                isOpen={isCreateBrandVoiceModalOpen}
                                onClose={() => setIsCreateBrandVoiceModalOpen(false)}
                                onSuccess={async (brandVoiceId: string) => {
                                    const voices = await getBrandVoices();
                                    setBrandVoices(voices);
                                    if (setFieldValueRef.current) {
                                        setFieldValueRef.current("brandVoiceId", brandVoiceId || "");
                                    }
                                }}
                            />
                            <EditAudienceProfileModal
                                isOpen={isCreateAudienceProfileModalOpen}
                                onClose={() => setIsCreateAudienceProfileModalOpen(false)}
                                audienceProfile={null}
                                browserLanguage={i18n.language}
                                onProfileCreated={async (profile: AudienceProfile) => {
                                    const profiles = await getAudienceProfiles();
                                    setAudienceProfiles(profiles);
                                    if (setFieldValueRef.current) {
                                        setFieldValueRef.current("audienceProfileId", profile.id || "");
                                    }
                                }}
                            />
                        </>
                    );
                }}
            </Formik>
        </div>
    );
};

export default TextCreation;
